import { useEffect, useState } from "react";
import { useListenStyles } from "./listenStil";
import {
  ladeKontaktStammdaten,
  ladeMitarbeiter,
  type KontaktKonto,
  type KontaktPerson,
} from "./kontaktStammdaten";

/**
 * Auswahlfelder, die aus einem gewählten Kontakt gespeist werden: Hauskonto
 * aus den Bankverbindungen des Eigentümers, Ansprechpartner aus den
 * Mitarbeitern der Verwaltung. Ohne Kontakt (oder auf dem öffentlichen Weg)
 * bleibt es beim Freitext — „anderes Konto" / „andere Person" ist immer
 * wählbar, eine Auswahl ist nie Pflicht.
 */

const ANDERES = "__anderes__";

type HauskontoProps = {
  /** Kontakt des Eigentümers; `null` = kein Kontakt gewählt. */
  contactId: number | null;
  /** Kontaktbestand abfragen? Nur auf dem angemeldeten Weg. */
  lookup: boolean;
  werte: { hausKontoBankId?: unknown; hausKontoBank?: unknown; hausKontoIban?: unknown };
  /** Schreibt alle drei Schlüssel in EINEM Vorgang. */
  onChange: (patch: { hausKontoBankId: string; hausKontoBank: string; hausKontoIban: string }) => void;
};

export const HauskontoAuswahl = ({ contactId, lookup, werte, onChange }: HauskontoProps) => {
  const styles = useListenStyles();
  const [konten, setKonten] = useState<KontaktKonto[]>([]);
  const [freiGewaehlt, setFreiGewaehlt] = useState(false);

  useEffect(() => {
    if (!lookup || !contactId) {
      setKonten([]);
      return;
    }
    let aktiv = true;
    ladeKontaktStammdaten(contactId)
      .then((r) => aktiv && setKonten(r.konten))
      .catch(() => aktiv && setKonten([]));
    return () => {
      aktiv = false;
    };
  }, [contactId, lookup]);

  const bankId = String(werte.hausKontoBankId ?? "");
  const bank = String(werte.hausKontoBank ?? "");
  const iban = String(werte.hausKontoIban ?? "");
  const gewaehlt = konten.find((k) => String(k.id) === bankId);
  const auswahl = gewaehlt ? bankId : konten.length > 0 && (freiGewaehlt || bank || iban) ? ANDERES : "";
  const zeigeFreitext = konten.length === 0 || auswahl === ANDERES;

  return (
    <div className={styles.grid}>
      {konten.length > 0 && (
        <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
          <span className={styles.label}>Konto aus den Bankverbindungen des Eigentümers</span>
          <select
            className={styles.input}
            name="hausKontoBankId"
            aria-label="Hauskonto — Bankverbindung des Eigentümers"
            value={auswahl}
            onChange={(e) => {
              const v = e.target.value;
              if (v === ANDERES) {
                setFreiGewaehlt(true);
                onChange({ hausKontoBankId: "", hausKontoBank: bank, hausKontoIban: iban });
                return;
              }
              setFreiGewaehlt(false);
              const k = konten.find((x) => String(x.id) === v);
              onChange({
                hausKontoBankId: k ? String(k.id) : "",
                hausKontoBank: k?.bankName ?? "",
                hausKontoIban: k?.iban ?? "",
              });
            }}
          >
            <option value="">Bitte wählen</option>
            {konten.map((k) => (
              <option key={k.id} value={String(k.id)}>
                {k.label}
                {k.isDefault ? " (Standard)" : ""}
              </option>
            ))}
            <option value={ANDERES}>Anderes Konto eingeben</option>
          </select>
        </div>
      )}
      {zeigeFreitext && (
        <>
          <div className={styles.field}>
            <span className={styles.label}>Bank</span>
            <input
              className={styles.input}
              name="hausKontoBank"
              aria-label="Hauskonto — Bank"
              value={bank}
              onChange={(e) => onChange({ hausKontoBankId: "", hausKontoBank: e.target.value, hausKontoIban: iban })}
            />
          </div>
          <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span className={styles.label}>IBAN</span>
            <input
              className={styles.input}
              name="hausKontoIban"
              aria-label="Hauskonto — IBAN"
              value={iban}
              onChange={(e) => onChange({ hausKontoBankId: "", hausKontoBank: bank, hausKontoIban: e.target.value })}
            />
          </div>
        </>
      )}
    </div>
  );
};

type AnsprechpartnerProps = {
  /** Kontakt der Verwaltung; `null` = keiner gewählt. */
  contactId: number | null;
  lookup: boolean;
  werte: { hvAnsprechpartner?: unknown; hvAnsprechpartnerId?: unknown };
  onChange: (patch: { hvAnsprechpartner: string; hvAnsprechpartnerId: string }) => void;
};

export const AnsprechpartnerAuswahl = ({ contactId, lookup, werte, onChange }: AnsprechpartnerProps) => {
  const styles = useListenStyles();
  const [personen, setPersonen] = useState<KontaktPerson[]>([]);
  const [freiGewaehlt, setFreiGewaehlt] = useState(false);

  useEffect(() => {
    if (!lookup || !contactId) {
      setPersonen([]);
      return;
    }
    let aktiv = true;
    ladeMitarbeiter(contactId)
      .then((r) => aktiv && setPersonen(r))
      .catch(() => aktiv && setPersonen([]));
    return () => {
      aktiv = false;
    };
  }, [contactId, lookup]);

  const name = String(werte.hvAnsprechpartner ?? "");
  const id = String(werte.hvAnsprechpartnerId ?? "");
  const gewaehlt = personen.find((p) => String(p.id) === id);
  const auswahl = gewaehlt ? id : personen.length > 0 && (freiGewaehlt || name) ? ANDERES : "";

  return (
    <div className={styles.grid}>
      {personen.length > 0 && (
        <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
          <span className={styles.label}>Ansprechpartner der Verwaltung</span>
          <select
            className={styles.input}
            name="hvAnsprechpartnerId"
            aria-label="Ansprechpartner der Verwaltung"
            value={auswahl}
            onChange={(e) => {
              const v = e.target.value;
              if (v === ANDERES) {
                setFreiGewaehlt(true);
                onChange({ hvAnsprechpartner: name, hvAnsprechpartnerId: "" });
                return;
              }
              setFreiGewaehlt(false);
              const p = personen.find((x) => String(x.id) === v);
              onChange({ hvAnsprechpartner: p?.label ?? "", hvAnsprechpartnerId: p ? String(p.id) : "" });
            }}
          >
            <option value="">Bitte wählen</option>
            {personen.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.label}
              </option>
            ))}
            <option value={ANDERES}>Andere Person eingeben</option>
          </select>
        </div>
      )}
      {(personen.length === 0 || auswahl === ANDERES) && (
        <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
          <span className={styles.label}>Ansprechpartner</span>
          <input
            className={styles.input}
            name="hvAnsprechpartner"
            aria-label="Ansprechpartner — Name"
            value={name}
            onChange={(e) => onChange({ hvAnsprechpartner: e.target.value, hvAnsprechpartnerId: "" })}
          />
        </div>
      )}
    </div>
  );
};
